// Layer 3 - 3 years - Matching, simple puzzles, pretend play (12 games total)
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useGame } from "@/context/GameContext";
import { playSfx, speak } from "@/lib/sounds";
import { t } from "@/lib/translations";

const playSfxHelper = (name, soundOn) => {
  try {
    playSfx(name, soundOn);
  } catch (e) {
    console.warn("Sound play failed", e);
  }
};

// ==========================================
// 1) Match Colors
// ==========================================
function MatchColors({ layer, onWin }) {
  const { language, soundOn } = useGame();
  const colors = [
    { id: "red", hex: "#F87171", nameId: "Merah", nameEn: "Red" },
    { id: "blue", hex: "#60A5FA", nameId: "Biru", nameEn: "Blue" },
    { id: "yellow", hex: "#FACC15", nameId: "Kuning", nameEn: "Yellow" },
    { id: "green", hex: "#4ADE80", nameId: "Hijau", nameEn: "Green" },
  ];
  const [target, setTarget] = useState(colors[Math.floor(Math.random() * 4)]);
  const [score, setScore] = useState(0);
  const label = language === "id" ? target.nameId : target.nameEn;

  const pick = (c) => {
    if (c.id === target.id) {
      playSfxHelper("success", soundOn);
      const s = score + 1;
      setScore(s);
      if (s >= 4) {
        setTimeout(() => onWin(), 1200);
        setScore(0);
      } else {
        let next;
        do {
          next = colors[Math.floor(Math.random() * 4)];
        } while (next.id === target.id);
        setTarget(next);
      }
    } else {
      playSfxHelper("wrong", soundOn);
    }
  };

  return (
    <div className="flex flex-col items-center mt-4" data-testid="game-match-color">
      <div className="font-display text-2xl sm:text-3xl font-bold text-slate-700 mb-2 text-center">
        {t("find_color", language)}
        <span className="ml-2 font-black" style={{ color: target.hex }}>{label}</span>
      </div>
      <div className="grid grid-cols-2 gap-5 max-w-md mt-4">
        {colors.map((c) => (
          <motion.button
            key={c.id}
            whileHover={{ y: -4 }}
            whileTap={{ scale: 0.9 }}
            onClick={() => pick(c)}
            className="w-32 h-32 sm:w-36 sm:h-36 rounded-3xl border-4 border-white shadow-xl"
            style={{ background: c.hex }}
            data-testid={`color-${c.id}`}
            aria-label={c.id}
          />
        ))}
      </div>
      <div className="mt-4 font-bold text-slate-500">⭐ {score}/4</div>
    </div>
  );
}

// ==========================================
// 2) Mini Puzzle
// ==========================================
function MiniPuzzle({ layer, onWin }) {
  const { language, soundOn } = useGame();
  const pieces = [
    { id: 0, emoji: "🍎" },
    { id: 1, emoji: "🍌" },
    { id: 2, emoji: "🍓" },
  ];
  const [placed, setPlaced] = useState({});
  const [selected, setSelected] = useState(null);

  const place = (slotId) => {
    if (selected === null) return;
    if (selected.id === slotId) {
      playSfxHelper("success", soundOn);
      const next = { ...placed, [slotId]: selected.emoji };
      setPlaced(next);
      setSelected(null);
      if (Object.keys(next).length === 3) {
        setTimeout(() => onWin(), 1200);
      }
    } else {
      playSfxHelper("wrong", soundOn);
    }
  };

  return (
    <div className="flex flex-col items-center mt-4" data-testid="game-puzzle">
      <div className="font-display text-xl sm:text-2xl font-bold text-slate-700 mb-3 text-center">
        {t("drag_pieces", language)}
      </div>
      <div className="flex gap-4 mb-6">
        {pieces.map((p) => (
          <button
            key={p.id}
            onClick={() => {
              playSfxHelper("tap", soundOn);
              setSelected(p);
            }}
            className="w-20 h-20 rounded-2xl bg-white border-4 flex items-center justify-center text-5xl"
            style={{
              borderColor: selected?.id === p.id ? "#22C55E" : "#bbf7d0",
              boxShadow: "0 5px 0 #86efac",
              opacity: placed[p.id] ? 0.3 : 1,
            }}
            disabled={!!placed[p.id]}
            data-testid={`puzzle-piece-${p.id}`}
          >
            {placed[p.id] ? "" : p.emoji}
          </button>
        ))}
      </div>
      <div className="flex gap-3">
        {pieces.map((p) => (
          <motion.button
            key={p.id}
            whileTap={{ scale: 0.95 }}
            onClick={() => place(p.id)}
            className="w-24 h-24 rounded-3xl border-4 border-dashed flex items-center justify-center text-5xl bg-white"
            style={{ borderColor: "#22C55E" }}
            data-testid={`puzzle-slot-${p.id}`}
          >
            {placed[p.id] || <span className="text-2xl opacity-30">{p.id + 1}</span>}
          </motion.button>
        ))}
      </div>
    </div>
  );
}

// ==========================================
// 3) Feed the Baby
// ==========================================
function FeedBaby({ layer, onWin }) {
  const { language, soundOn } = useGame();
  const foods = ["🍎", "🥕", "🍓", "🍞", "🥛", "🍌"];
  const [fed, setFed] = useState(0);
  const [recent, setRecent] = useState(null);

  const feed = (f) => {
    playSfxHelper("giggle", soundOn);
    setRecent(f);
    const n = fed + 1;
    setFed(n);
    setTimeout(() => setRecent(null), 700);
    if (n >= 4) {
      setTimeout(() => onWin(), 1200);
    }
  };

  return (
    <div className="flex flex-col items-center mt-4" data-testid="game-feed">
      <div className="font-display text-xl sm:text-2xl font-bold text-slate-700 mb-2 text-center">
        {t("give_food", language)}
      </div>
      <div className="relative">
        <motion.img
          src={layer.baby}
          alt="baby"
          animate={{ scale: recent ? [1, 1.1, 1] : 1 }}
          className="w-44 h-44 sm:w-56 sm:h-56 rounded-full object-cover border-[6px] border-white shadow-2xl"
          style={{ borderColor: "#22C55E" }}
          draggable={false}
        />
        {recent && (
          <motion.div
            initial={{ y: -80, opacity: 1, scale: 1 }}
            animate={{ y: 0, opacity: 0, scale: 0.6 }}
            transition={{ duration: 0.7 }}
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-5xl"
          >
            {recent}
          </motion.div>
        )}
      </div>
      <div className="grid grid-cols-3 gap-3 mt-6 max-w-md">
        {foods.map((f, i) => (
          <motion.button
            key={`food-${f}-${i}`}
            whileTap={{ scale: 0.85 }}
            onClick={() => feed(f)}
            className="w-20 h-20 sm:w-24 sm:h-24 rounded-3xl bg-white border-4 flex items-center justify-center text-5xl"
            style={{ borderColor: "#22C55E", boxShadow: `0 5px 0 #15803d` }}
            data-testid={`food-${i}`}
          >
            {f}
          </motion.button>
        ))}
      </div>
      <div className="mt-3 font-bold text-slate-500">🍽️ {fed}/4</div>
    </div>
  );
}

// ==========================================
// 4) Animal Tooth Match (Game 4)
// ==========================================
function AnimalToothMatch({ layer, onWin }) {
  const { language, soundOn } = useGame();
  const [tapped, setTapped] = useState(false);

  const handleToothClick = (isSick) => {
    if (isSick) {
      playSfxHelper("success", soundOn);
      setTapped(true);
      setTimeout(() => onWin(), 1200);
    } else {
      playSfxHelper("wrong", soundOn);
    }
  };

  return (
    <div className="flex flex-col items-center gap-6 mt-4">
      <div className="font-display text-xl sm:text-2xl font-bold text-slate-700 text-center">
        {language === "id" ? "Ketuk Gigi Buaya yang Sakit! 🐊" : "Tap the Croc's Red Tooth! 🐊"}
      </div>

      <div className="w-64 h-56 rounded-3xl bg-emerald-100 border-4 border-emerald-400 shadow-xl flex flex-col justify-between p-6 relative overflow-hidden">
        {/* Crocodile Head representation */}
        <span className="text-8xl text-center select-none">🐊</span>
        
        {/* Teeth row */}
        <div className="flex justify-center gap-2 mt-4 z-10">
          <button
            onClick={() => handleToothClick(false)}
            className="w-10 h-10 bg-white border-2 border-slate-300 rounded-b-xl shadow"
          />
          <button
            onClick={() => handleToothClick(true)}
            disabled={tapped}
            className={`w-10 h-10 border-2 rounded-b-xl shadow transition-colors ${tapped ? "bg-white border-slate-300" : "bg-red-500 border-red-600"}`}
          />
          <button
            onClick={() => handleToothClick(false)}
            className="w-10 h-10 bg-white border-2 border-slate-300 rounded-b-xl shadow"
          />
        </div>
      </div>

      <div className="font-bold text-slate-500 text-xs">
        {language === "id" ? "Melatih Kontrol Inhibisi & Akurasi Motorik" : "Trains Inhibitory Control & Motor Accuracy"}
      </div>
    </div>
  );
}

// ==========================================
// 5) Bubble Bath (Game 5)
// ==========================================
function BubbleBath({ layer, onWin }) {
  const { language, soundOn } = useGame();
  const [popped, setPopped] = useState([]);
  const bubbles = [1, 2, 3, 4, 5];

  const handlePop = (id) => {
    playSfxHelper("pop", soundOn);
    setPopped((prev) => {
      const next = [...prev, id];
      if (next.length === bubbles.length) {
        setTimeout(() => onWin(), 1000);
      }
      return next;
    });
  };

  return (
    <div className="flex flex-col items-center gap-6 mt-4">
      <div className="font-display text-xl sm:text-2xl font-bold text-slate-700 text-center">
        {language === "id" ? "Pecahkan Gelembung Mandi Bebek! 🛁" : "Pop Duck's Bath Bubbles! 🛁"} ({popped.length}/5)
      </div>

      <div className="relative w-64 h-64 sm:w-80 sm:h-80 bg-gradient-to-b from-blue-200 to-sky-100 rounded-3xl border-4 border-white shadow-2xl flex items-center justify-center overflow-hidden">
        <span className="text-8xl select-none animate-bounce">🦆</span>

        {/* Bubbles */}
        {bubbles.map((b) => {
          const isPopped = popped.includes(b);
          if (isPopped) return null;
          return (
            <motion.button
              key={b}
              onClick={() => handlePop(b)}
              whileTap={{ scale: 0.8 }}
              className="absolute w-12 h-12 rounded-full bg-white/40 border border-white/60 shadow flex items-center justify-center text-xl select-none"
              style={{
                left: `${15 + b * 13}%`,
                top: `${20 + (b % 3) * 22}%`,
              }}
            >
              🫧
            </motion.button>
          );
        })}
      </div>

      <div className="font-bold text-slate-500 text-xs">
        {language === "id" ? "Meningkatkan Fokus Atensi & Regulasi Diri" : "Boosts Attention Focus & Self-Regulation"}
      </div>
    </div>
  );
}

// ==========================================
// 6) Dress Up Mascot (Game 6)
// ==========================================
function DressUpMascot({ layer, onWin }) {
  const { language, soundOn } = useGame();
  const [clothing, setClothing] = useState(null);

  const handleDress = (type) => {
    playSfxHelper("success", soundOn);
    setClothing(type);
    setTimeout(() => onWin(), 1200);
  };

  return (
    <div className="flex flex-col items-center gap-6 mt-4">
      <div className="font-display text-xl sm:text-2xl font-bold text-slate-700 text-center">
        {language === "id" ? "Pakaikan Baju Bintang! 👕" : "Dress Up the Star Mascot! 👕"}
      </div>

      <div className="relative w-56 h-56 rounded-full bg-white border-4 border-yellow-200 shadow-xl flex items-center justify-center">
        <span className="text-9xl select-none">⭐</span>
        {clothing && (
          <motion.span
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="absolute text-7xl select-none bottom-4"
          >
            {clothing === "red" ? "👕" : "👚"}
          </motion.span>
        )}
      </div>

      <div className="flex gap-4">
        <motion.button
          whileTap={{ scale: 0.9 }}
          onClick={() => handleDress("red")}
          className="px-6 py-3 rounded-2xl bg-red-400 text-white font-display font-black text-lg shadow-lg border-2 border-white"
        >
          🔴 {language === "id" ? "Baju Merah" : "Red Shirt"}
        </motion.button>
        <motion.button
          whileTap={{ scale: 0.9 }}
          onClick={() => handleDress("blue")}
          className="px-6 py-3 rounded-2xl bg-sky-400 text-white font-display font-black text-lg shadow-lg border-2 border-white"
        >
          🔵 {language === "id" ? "Baju Biru" : "Blue Shirt"}
        </motion.button>
      </div>
    </div>
  );
}

// ==========================================
// 7) Bee Path Finder (Game 7)
// ==========================================
function BeePathFinder({ layer, onWin }) {
  const { language, soundOn } = useGame();
  const [success, setSuccess] = useState(false);

  const handlePath = () => {
    playSfxHelper("success", soundOn);
    setSuccess(true);
    setTimeout(() => onWin(), 1200);
  };

  return (
    <div className="flex flex-col items-center gap-6 mt-4">
      <div className="font-display text-xl sm:text-2xl font-bold text-slate-700 text-center">
        {language === "id" ? "Bantu Lebah Menuju Bunga! 🐝" : "Help the Bee Find the Flower! 🐝"}
      </div>

      <div className="w-72 h-56 rounded-3xl bg-amber-50/50 border-4 border-yellow-200 shadow-xl flex flex-col justify-between p-6 relative">
        <div className="flex justify-between items-center w-full">
          <motion.span
            animate={success ? { x: 180 } : {}}
            transition={{ duration: 1 }}
            className="text-5xl select-none"
          >
            🐝
          </motion.span>
          <span className="text-5xl select-none">🌸</span>
        </div>

        {/* Trail simulation */}
        <div className="w-full h-1 bg-dashed border-t-4 border-yellow-300 my-4" />

        <div className="flex justify-center">
          <motion.button
            whileTap={{ scale: 0.9 }}
            onClick={handlePath}
            disabled={success}
            className="px-6 py-2 rounded-2xl bg-yellow-500 text-white font-display font-black shadow-lg"
          >
            🎯 {language === "id" ? "Hubungkan" : "Connect"}
          </motion.button>
        </div>
      </div>

      <div className="font-bold text-slate-500 text-xs">
        {language === "id" ? "Melatih Penalaran Spasial & Estimasi Arah" : "Trains Spatial Reasoning & Directional Sense"}
      </div>
    </div>
  );
}

// ==========================================
// 8) Mini Angklung (Game 8)
// ==========================================
function MiniAngklung({ layer, onWin }) {
  const { language, soundOn } = useGame();
  const [played, setPlayed] = useState([]);
  const notes = [
    { id: 1, label: "Do", color: "bg-red-400", size: "h-48" },
    { id: 2, label: "Re", color: "bg-yellow-400", size: "h-40" },
    { id: 3, label: "Mi", color: "bg-sky-400", size: "h-32" },
  ];

  const handlePlay = (id) => {
    playSfxHelper("star", soundOn);
    setPlayed((prev) => {
      const next = prev.includes(id) ? prev : [...prev, id];
      if (next.length === notes.length) {
        setTimeout(() => onWin(), 1000);
      }
      return next;
    });
  };

  return (
    <div className="flex flex-col items-center gap-6 mt-4">
      <div className="font-display text-xl sm:text-2xl font-bold text-slate-700 text-center">
        {language === "id" ? "Ketuk Angklung Bambu! 🎋" : "Play the Bamboo Angklung! 🎋"} ({played.length}/3)
      </div>

      <div className="flex gap-6 items-end justify-center min-h-[14rem]">
        {notes.map((n) => (
          <motion.button
            key={n.id}
            onClick={() => handlePlay(n.id)}
            whileTap={{ rotate: [-8, 8, -8, 0], scale: 0.95 }}
            className={`w-14 ${n.size} ${n.color} rounded-t-3xl border-t-8 border-x-4 border-white/60 shadow-xl flex flex-col justify-end pb-4 items-center`}
          >
            <span className="text-white font-display font-black text-lg select-none">{n.label}</span>
          </motion.button>
        ))}
      </div>

      <div className="font-bold text-slate-500 text-xs">
        {language === "id" ? "Menstimulasi Musikalitas & Sensibilitas Pitch" : "Stimulates Musicality & Pitch Sensitivity"}
      </div>
    </div>
  );
}

// ==========================================
// 9) Insect Finder (Game 9)
// ==========================================
function InsectFinder({ layer, onWin }) {
  const { language, soundOn } = useGame();
  const [found, setFound] = useState(false);

  const handleFind = () => {
    playSfxHelper("success", soundOn);
    setFound(true);
    setTimeout(() => onWin(), 1200);
  };

  return (
    <div className="flex flex-col items-center gap-6 mt-4">
      <div className="font-display text-xl sm:text-2xl font-bold text-slate-700 text-center">
        {language === "id" ? "Temukan Kepik Merah di Daun! 🐞" : "Find the Ladybug on the Leaves! 🐞"}
      </div>

      <div className="relative w-64 h-64 rounded-3xl bg-green-100 border-4 border-green-300 shadow-xl overflow-hidden flex items-center justify-center">
        {/* Leaf decorations */}
        <span className="absolute text-5xl left-4 top-4 select-none opacity-40">🍃</span>
        <span className="absolute text-5xl right-6 bottom-4 select-none opacity-40">🍃</span>

        <motion.button
          onClick={handleFind}
          disabled={found}
          whileTap={{ scale: 0.8 }}
          animate={found ? { scale: [1, 1.3, 1], rotate: [0, 360, 0] } : {}}
          className="w-20 h-20 rounded-full flex items-center justify-center select-none"
        >
          <span className="text-6xl">🐞</span>
        </motion.button>
      </div>

      <div className="font-bold text-slate-500 text-xs">
        {language === "id" ? "Melatih Atensi Selektif (Visual Search)" : "Trains Selective Attention (Visual Search)"}
      </div>
    </div>
  );
}

// ==========================================
// 10) Rabbit Carrot Harvest (Game 10)
// ==========================================
function RabbitHarvest({ layer, onWin }) {
  const { language, soundOn } = useGame();
  const [harvested, setHarvested] = useState(0);

  const handleHarvest = () => {
    playSfxHelper("giggle", soundOn);
    const next = harvested + 1;
    setHarvested(next);
    if (next >= 4) {
      setTimeout(() => onWin(), 1200);
    }
  };

  return (
    <div className="flex flex-col items-center gap-6 mt-4">
      <div className="font-display text-xl sm:text-2xl font-bold text-slate-700 text-center">
        {language === "id" ? "Suapi Wortel ke Kelinci! 🐰" : "Feed Carrots to the Hungry Bunny! 🐰"} ({harvested}/4)
      </div>

      <div className="flex justify-between items-center w-full max-w-sm mt-4">
        <span className="text-8xl select-none animate-pulse">🐰</span>
        <motion.button
          onClick={handleHarvest}
          whileTap={{ scale: 0.85 }}
          className="w-24 h-24 rounded-full bg-white border-4 border-yellow-200 shadow-xl flex items-center justify-center text-5xl select-none"
        >
          🥕
        </motion.button>
      </div>

      <div className="font-bold text-slate-500 text-xs">
        {language === "id" ? "Memperkuat Perilaku Empati & Goal-Directed" : "Strengthens Empathic & Goal-Directed Behavior"}
      </div>
    </div>
  );
}

// ==========================================
// 11) Beat the Kendang (Game 11)
// ==========================================
function BeatKendang({ layer, onWin }) {
  const { language, soundOn } = useGame();
  const [beats, setBeats] = useState(0);

  const handleBeat = (type) => {
    playSfxHelper(type === "dung" ? "star" : "pop", soundOn);
    const next = beats + 1;
    setBeats(next);
    if (next >= 6) {
      setTimeout(() => onWin(), 1000);
    }
  };

  return (
    <div className="flex flex-col items-center gap-6 mt-4">
      <div className="font-display text-xl sm:text-2xl font-bold text-slate-700 text-center">
        {language === "id" ? "Tabuh Gendang Kendang! 🥁" : "Beat the Traditional Kendang! 🥁"} ({beats}/6)
      </div>

      <div className="flex gap-4 items-center">
        <motion.button
          whileTap={{ scale: 0.9, y: 10 }}
          onClick={() => handleBeat("dung")}
          className="w-28 h-28 rounded-full bg-amber-500 border-4 border-yellow-300 shadow-xl flex items-center justify-center text-white font-display font-black text-2xl"
        >
          DUNG!
        </motion.button>
        <motion.button
          whileTap={{ scale: 0.9, y: 10 }}
          onClick={() => handleBeat("tak")}
          className="w-28 h-28 rounded-full bg-orange-400 border-4 border-yellow-200 shadow-xl flex items-center justify-center text-white font-display font-black text-2xl"
        >
          TAK!
        </motion.button>
      </div>

      <div className="font-bold text-slate-500 text-xs">
        {language === "id" ? "Meningkatkan Sinkronisasi Sosio-Musikal" : "Improves Socio-Musical Synchronization"}
      </div>
    </div>
  );
}

// ==========================================
// 12) Paint Sweeper (Game 12)
// ==========================================
function PaintSweeper({ layer, onWin }) {
  const { language, soundOn } = useGame();
  const [revealed, setRevealed] = useState(false);
  const [patches, setPatches] = useState(() =>
    Array.from({ length: 9 }, (_, i) => ({
      id: i,
      wiped: false,
    }))
  );

  const paintPatch = (id) => {
    if (revealed) return;
    playSfxHelper("pop", soundOn);
    setPatches((prev) => {
      const next = prev.map((p) => (p.id === id ? { ...p, wiped: true } : p));
      const wipedCount = next.filter((p) => p.wiped).length;
      if (wipedCount === prev.length) {
        setRevealed(true);
        setTimeout(() => onWin(), 1200);
      }
      return next;
    });
  };

  return (
    <div className="flex flex-col items-center gap-6 mt-4">
      <div className="font-display text-xl sm:text-2xl font-bold text-slate-700 text-center">
        {language === "id" ? "Warnai Dinding Kaca! 🎨" : "Paint the Rainbow Glass! 🎨"}
      </div>

      <div className="relative w-64 h-64 sm:w-80 sm:h-80 rounded-3xl overflow-hidden border-4 border-white shadow-2xl bg-gradient-to-tr from-red-300 via-yellow-200 to-teal-300 flex items-center justify-center">
        <motion.div
          animate={revealed ? { scale: [1, 1.2, 1], rotate: [0, 10, -10, 0] } : {}}
          transition={{ duration: 1 }}
          className="text-8xl select-none"
        >
          🌈
        </motion.div>

        {!revealed && (
          <div className="absolute inset-0 grid grid-cols-3 grid-rows-3 gap-0.5 bg-slate-400">
            {patches.map((p) => (
              <motion.button
                key={p.id}
                onMouseEnter={() => paintPatch(p.id)}
                onTouchStart={() => paintPatch(p.id)}
                onClick={() => paintPatch(p.id)}
                animate={{ opacity: p.wiped ? 0 : 1 }}
                className="w-full h-full bg-slate-300 border border-slate-400/40 flex items-center justify-center text-2xl select-none"
              >
                🖌️
              </motion.button>
            ))}
          </div>
        )}
      </div>

      <div className="font-bold text-slate-500 text-xs">
        {language === "id" ? "Melatih Integrasi Sensori & Koordinasi Halus" : "Trains Sensory Integration & Fine Coordination"}
      </div>
    </div>
  );
}

// Export Layer 3 games list
export const Layer3Games = {
  match_color: { icon: "🎨", Component: MatchColors },
  puzzle: { icon: "🧩", Component: MiniPuzzle },
  feed: { icon: "🍼", Component: FeedBaby },
  tooth_match: { icon: "🐊", Component: AnimalToothMatch },
  bubble_bath: { icon: "🛁", Component: BubbleBath },
  dress_up: { icon: "👕", Component: DressUpMascot },
  bee_maze: { icon: "🐝", Component: BeePathFinder },
  angklung: { icon: "🎵", Component: MiniAngklung },
  insect_finder: { icon: "🐞", Component: InsectFinder },
  rabbit_harvest: { icon: "🐰", Component: RabbitHarvest },
  kendang: { icon: "🥁", Component: BeatKendang },
  paint_sweeper: { icon: "🎨", Component: PaintSweeper },
};
